import { derived } from 'svelte/store';
import {
  createFeed,
  createFilteredFeed,
  createSingleRaceFeed,
} from '../stores.js';
import { findElementInShadowDom, renderSvelteComponent } from './render.js';
import { EXCLUDED_RACE_IDS } from '../excluded-races.js';
import RaceList from '../components/RaceList.svelte';

// TEMPORARY: AP's jurisdiction for shared races doesn't match L.A. Vote's, so
// the dropdown would list the same city twice. Until that's fixed in the
// data, an AP race shown in place of an L.A. Vote race keeps L.A. Vote's
// jurisdiction. To remove: delete this function and its one call below.
/**
 * @param {import('../types').Race} apRace
 * @param {import('../types').Race} laRace
 * @returns {import('../types').Race}
 */
function withLaJurisdiction(apRace, laRace) {
  return {
    ...apRace,
    attributes: {
      ...apRace.attributes,
      jurisdiction: laRace.attributes.jurisdiction,
    },
  };
}

/**
 * Finds a multi-race embed's box and fills it with the races that match
 * `filter`, straight from the live feed. Updates when the feed polls.
 *
 * TO ADD A NEW MULTI-RACE EMBED:
 * 1. Copy an existing embed file (e.g. `scripts/embeds-boe.js`), rename it,
 *    and change the embed name, feed and filter.
 * 2. Add its name (without `.js`) to `entrypoints` in `baker.config.js`.
 * 3. Add an entry to `_data/embeds/multipleRaces.js` with `slug`, and in
 *    `embed_meta`: `js` (the file name) and `unique_identifier` (the same
 *    embed name passed below), plus `legend: true` for the winner key.
 *
 * `embedName` must match `unique_identifier`, which race_template.html writes
 * as `data-embed-name` on the embed's box.
 *
 * CAPTION NOTE
 * **** THIS IS FOR IF WE WANT TO PUT SOME CUSTOM TEXT ON A RESULT *******
 * a grey line under a race's caption that explains something
 * about the race, usually how it's won. In the June 2026 primary, the
 * Superintendent of Public Instruction (SUE) race had: 'A candidate can win
 * by getting a majority of the vote. If no one receives 50% + 1, the top two
 * advance to November election.'
 *
 * Key each note by the race's id in the feed; races not listed get no note.
 * Find the id in the feed (the race's `id`, e.g. 'CA-6482' for AP, or
 * 'sheriff-4348' for L.A. Vote):
 *
 *   mountRaceList('ca-statewide-races', STATE_URL, {
 *     filter: { ... },
 *     component: 'result',
 *     captionNote: {
 *       'CA-84302': 'A candidate can win by getting a majority of the vote.',
 *       'CA-6489': 'Another note for another race.',
 *     },
 *   });
 *
 * GROUPS AND PINNED RACES: `filter` can be a list of filters; each one's races
 * are shown as a group, in the order listed. `pinnedRaces` are read from
 * their own race files (RACE_URL), so they can come from another feed, and
 * are shown before everything else. Not used by any embed yet. E.g. one AP
 * race on top of an L.A. Vote list, then two groups:
 *
 *   mountRaceList('example-embed', LA_COUNTY_URL, {
 *     pinnedRaces: [{ id: 'CA-84304' }],
 *     filter: [
 *       { tagsAny: ['losAngelesCounty'], electionType: 'measure' },
 *       { tagsAny: ['schools'], sort: 'title' },
 *     ],
 *     component: 'result',
 *   });
 *
 * SHARED RACES: races both AP and L.A. Vote report, listed with both ids
 * in scripts/shared-races.js. List the L.A. Vote id in `filter` as usual; when
 * the race's `use` is 'ap', AP's race is shown in its place: AP's title,
 * votes, calls and % of expected vote. Each AP race comes from its own race
 * file (RACE_URL). If AP hasn't published it yet, L.A. Vote's race stays.
 *
 *   import { SHARED_RACES } from '../shared-races.js';
 *
 *   mountRaceList('la-city-races', LA_CITY_URL, {
 *     filter: { ids: SHARED_RACES.map((race) => race.lavote) },
 *     component: 'result',
 *     sharedRaces: SHARED_RACES,
 *   });
 *
 * @param {string} embedName - Value of data-embed-name on the box
 * @param {string} feedUrl - Feed to fetch, e.g. STATE_URL
 * @param {Object} options
 * @param {import('../stores.js').FilterSpec | import('../stores.js').FilterSpec[]} options.filter -
 *   Which races, in what order; a list shows each filter's races as a group (see GROUPS above)
 * @param {{ id: string, source?: string }[]} [options.pinnedRaces] - Races shown first,
 *   from their own race files; `source` is 'ap' (default) or 'lavote'. One
 *   that doesn't load is skipped (logged to the console).
 * @param {import('../shared-races.js').SharedRace[]} [options.sharedRaces] -
 *   Races AP and L.A. Vote both report; ones set to 'ap' show AP's race (see SHARED RACES above)
 * @param {'result' | 'proposition'} options.component - How each race is drawn
 * @param {Record<string, string>} [options.captions] - Custom captions keyed by race id
 * @param {Record<string, string>} [options.captionNote] - Grey notes under the caption, keyed by race id (see CAPTION NOTES above)
 * @param {'title' | 'jurisdiction'} [options.captionField] - Feed field for the other captions:
 *   'title' ('Board of Equalization — Seat 1', the default) or 'jurisdiction' ('Seat 1')
 * @param {string} [options.dropdown] - Adds a dropdown to filter by city or district
 *   (jurisdiction); the word after "Show all", e.g. 'cities'. Leave out for none.
 */
export async function mountRaceList(
  embedName,
  feedUrl,
  {
    filter,
    pinnedRaces = [],
    sharedRaces = [],
    component,
    captions = {},
    captionNote = {},
    captionField = 'title',
    dropdown = '',
  }
) {
  const container = findElementInShadowDom(`[data-embed-name='${embedName}']`);
  if (!container) return;

  const feed = await createFeed(feedUrl);
  const filters = Array.isArray(filter) ? filter : [filter];
  // Races in EXCLUDED_RACE_IDS are dropped from every filter
  let groups = filters.map((spec) =>
    createFilteredFeed(feed, {
      ...spec,
      excludeIds: [...EXCLUDED_RACE_IDS, ...(spec.excludeIds ?? [])],
    })
  );

  // Show AP's race in place of L.A. Vote's for shared races set to 'ap'.
  // Each AP race comes from its own race file (RACE_URL), so only those races
  // are downloaded, and embeds on the same page share the fetch. One that
  // doesn't load keeps L.A. Vote's race. Updates when any feed polls.
  const apIdFor = new Map(
    sharedRaces
      .filter((race) => race.use === 'ap')
      .map((race) => [race.lavote, race.ap])
  );
  if (apIdFor.size) {
    const apStores = await Promise.all(
      [...apIdFor.values()].map((apId) =>
        createSingleRaceFeed(apId).catch((error) => {
          console.error(
            `AP race ${apId} didn't load; showing L.A. Vote's`,
            error
          );
          return null;
        })
      )
    );
    const apFeeds =
      /** @type {import('svelte/store').Readable<import('../types').Race>[]} */ (
        apStores.filter(Boolean)
      );
    // AP id -> AP race
    const apRaces = derived(
      apFeeds,
      /** @param {import('../types').Race[]} $apRaces */
      ($apRaces) =>
        new Map($apRaces.filter(Boolean).map((race) => [race.id, race]))
    );
    groups = groups.map((group) =>
      derived([group, apRaces], ([$group, $apRaces]) =>
        $group.map((race) => {
          const apId = apIdFor.get(race.id);
          const apRace = apId && $apRaces.get(apId);
          return apRace ? withLaJurisdiction(apRace, race) : race;
        })
      )
    );
  }

  // A pinned race whose file doesn't load is skipped, not the whole embed
  const pinned =
    /** @type {import('svelte/store').Readable<import('../types').Race>[]} */ (
      (
        await Promise.all(
          pinnedRaces.map(({ id, source }) =>
            createSingleRaceFeed(id, source).catch((error) => {
              console.error(
                `Pinned race ${id} didn't load; skipping it`,
                error
              );
              return null;
            })
          )
        )
      ).filter(Boolean)
    );

  // pinned races first, then each group in order; updates when any feed polls
  const races = derived([...pinned, ...groups], ($stores) => {
    const $pinned = /** @type {import('../types').Race[]} */ (
      $stores.slice(0, pinned.length).filter(Boolean)
    );
    const $groups = /** @type {import('../types').Race[][]} */ (
      $stores.slice(pinned.length)
    );
    return [...$pinned, ...$groups.flat()];
  });

  renderSvelteComponent(RaceList, container, {
    races,
    component,
    captions,
    captionNote,
    captionField,
    dropdown,
  });
}
